import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Search, MapPin, Navigation, Copy, Route as RouteIcon, LocateFixed,
    AlertTriangle, Loader2, Check, Phone, MessageCircle, ExternalLink, Smartphone,
    Upload, ShieldCheck, UserCheck, Lock, Flag, X, Users, Merge,
} from 'lucide-react';
import { useToast } from '../../../../src/contexts/ToastContext';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { getOrigin, hojeLocalIso, type Origem } from '../../../../src/lib/rotaInteligente';
import {
    geocodificarUcs, aplicarGeocode, montarPreviaRota, salvarRotaCobranca,
    ultimaTratativaPorCpf, ucsQuePrecisamGeocode, hidratarCacheGeocode, termosPorUc,
    MAX_PARADAS_ROUTES, carregarRotaCobranca, valorDaParada, faturasDaParada, listarEquipeCobranca,
    sugerirEnderecos, localizarSugestao,
    type ParadaPrevia, type PreviaRotaCobranca, type RotaSalva,
} from '../../../../src/lib/rotaCobranca';
import { paradasEconomizadas, ucsDaParada } from '../../../../src/lib/agrupamentoRota';
import { mensagemRota, cabecalhoRota, cabeNoLinkWhatsapp, escolherFormatoMensagem } from '../../../../src/lib/mensagemCobranca';
import { copyToClipboard } from '../../../../src/lib/clipboardShare';
import EnviarWhatsapp from '../../../../src/components/EnviarWhatsapp';
import ConferenciaRotaModal from '../components/ConferenciaRotaModal';
import { googleMapsDirTrechos, GOOGLE_MAPS_MAX_STOPS } from '../../../../src/lib/routeOptimizer';
import { linkWhatsappUc, formatarTelefoneBr, urlNavegacaoUc } from '../../../../src/lib/enderecoUc';
import {
    APPS_NAVEGACAO, appNavegacaoPreferido, salvarAppNavegacao, suportaRotaEncadeada,
    navUrlCoordenada, type AppNavegacao,
} from '../../../../src/lib/navegacaoMaps';
import { lerUcsDaPlanilha } from '../../../../src/lib/importarPlanilhaUcs';
import { consumirPreselecao } from '../../../../src/lib/preselecaoRota';
import { normalizeUcCodigo } from '../../../../src/lib/ucCodigo';
import { baseUrlPublica } from '../../../../src/lib/urlApp';
import { UcInadimplente } from '../types';
import type { RecorteDaTela } from '../lib/perfisExportacao';
import { formatCurrency } from '../utils';

interface Props {
    ucs: UcInadimplente[];
    cidadeFiltro: string;
    /** Avisa o cabeçalho o que esta aba mostra (marcadas ou filtradas), para o Excel sair igual. */
    onRecorteChange?: (recorte: RecorteDaTela) => void;
}


/** Onde a rota termina. `partida` = volta ao ponto de partida (o escritório, quase sempre). */
type ModoChegada = 'ultima' | 'partida' | 'endereco';

const OPCOES_CHEGADA: Array<{ valor: ModoChegada; rotulo: string }> = [
    { valor: 'ultima', rotulo: 'Na última parada' },
    { valor: 'partida', rotulo: 'Volta à partida' },
    { valor: 'endereco', rotulo: 'Outro endereço' },
];

const RotaCobranca: React.FC<Props> = ({ ucs, cidadeFiltro, onRecorteChange }) => {
    const { addToast } = useToast();
    const { profile } = useAuth();

    const [busca, setBusca] = useState('');
    const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());
    const [origem, setOrigem] = useState<Origem | null>(null);
    const [origemLabel, setOrigemLabel] = useState<string | null>(null);
    const [buscandoGps, setBuscandoGps] = useState(false);

    const [enderecoOrigem, setEnderecoOrigem] = useState('');
    const [sugestoes, setSugestoes] = useState<Array<{ placeId: string; text: string }>>([]);

    // Ponto de chegada — pedido de 16/09/2026: "só tem ponto de partida". Sem ele a
    // rota termina na última parada, que é o comportamento de antes.
    const [modoChegada, setModoChegada] = useState<ModoChegada>('ultima');
    const [chegadaEndereco, setChegadaEndereco] = useState<(Origem & { label: string }) | null>(null);
    const [textoChegada, setTextoChegada] = useState('');
    const [sugestoesChegada, setSugestoesChegada] = useState<Array<{ placeId: string; text: string }>>([]);

    // Cliente com várias UCs em débito vira UMA parada que cobra todas (pedido
    // de 16/09/2026, caso Max Emiliano). Desligável: há cliente com UCs em
    // cidades diferentes, e aí são visitas diferentes mesmo.
    const [juntarPorCliente, setJuntarPorCliente] = useState(true);

    const [gerando, setGerando] = useState(false);
    const [etapa, setEtapa] = useState('');
    const [previa, setPrevia] = useState<PreviaRotaCobranca | null>(null);
    const [rotaId, setRotaId] = useState<string | null>(null);
    const [mensagem, setMensagem] = useState('');
    const [salvando, setSalvando] = useState(false);

    // A prévia nasce DEPOIS da tabela no documento. Com a lista cheia — e ela
    // costuma estar, são centenas de UCs vencidas — a rota pronta aparece bem
    // abaixo da dobra, enquanto o botão que a gerou fica fixo no rodapé. Sem
    // rolar até ela, gerar a rota parece não ter feito nada.
    const previaRef = useRef<HTMLElement | null>(null);

    // Em qual app os links vão abrir. Lazy: a leitura do localStorage e a
    // detecção de iOS não precisam repetir a cada render.
    const [appNav, setAppNav] = useState<AppNavegacao>(() => appNavegacaoPreferido());
    const [importando, setImportando] = useState(false);
    const inputPlanilhaRef = useRef<HTMLInputElement | null>(null);

    // Para quem é a rota. Quem monta nem sempre é quem dirige — foi o pedido que
    // originou tudo isto. Sem escolha explícita, é para si mesmo.
    const [responsavelId, setResponsavelId] = useState<string>('');
    const [equipe, setEquipe] = useState<Array<{ id: string; full_name: string | null }>>([]);
    const [conferindo, setConferindo] = useState<string | null>(null);
    // A mensagem só existe depois da liberação. Antes disso ela carregaria o link
    // do Maps de ANTES da correção do endereço — e sairia para o cobrador sem
    // ninguém ter conferido nada, que é exatamente o que a conferência impede.
    const [liberada, setLiberada] = useState(false);

    /** Só adm e gerente mandam rota para terceiros. Analista monta a própria — é
     *  o mesmo corte de `podeGerenciarCobranca` na Central, um degrau acima. */
    const podeAtribuir = profile?.role === 'adm' || profile?.role === 'gerente';

    useEffect(() => {
        if (!podeAtribuir) return;
        let vivo = true;
        void (async () => {
            const lista = await listarEquipeCobranca().catch(() => []);
            if (vivo) setEquipe(lista);
        })();
        return () => { vivo = false; };
    }, [podeAtribuir]);

    const responsavelEfetivo = responsavelId || profile?.id || '';
    const responsavelNome = responsavelId
        ? (equipe.find(p => p.id === responsavelId)?.full_name || 'o responsável')
        : 'você';

    // "Volta à partida" sem partida definida não tem para onde voltar: a rota
    // fica aberta e a tela avisa, em vez de inventar um destino.
    const destino: Origem | null = modoChegada === 'partida' ? origem
        : modoChegada === 'endereco' && chegadaEndereco ? { lat: chegadaEndereco.lat, lng: chegadaEndereco.lng }
            : null;
    const destinoLabel = modoChegada === 'partida' && origem
        ? `Volta à partida${origemLabel ? ` (${origemLabel})` : ''}`
        : modoChegada === 'endereco' ? chegadaEndereco?.label ?? null
            : null;

    const visiveis = useMemo(() => {
        if (!busca.trim()) return ucs;
        const b = busca.trim().toLowerCase();
        const digitos = busca.replace(/\D/g, '');
        return ucs.filter(uc =>
            uc.clienteNome.toLowerCase().includes(b)
            || uc.ucCodigo.toLowerCase().includes(b)
            || (uc.titular || '').toLowerCase().includes(b)
            || (uc.enderecoTexto || '').toLowerCase().includes(b)
            || (digitos.length >= 3 && (uc.cpfCnpj || '').includes(digitos)));
    }, [ucs, busca]);

    const escolhidas = useMemo(
        () => ucs.filter(uc => selecionadas.has(uc.chave)),
        [ucs, selecionadas]);

    // O botão "Exportar Excel" fica no cabeçalho da Central, fora desta tela.
    // Sem isto ele não saberia quais paradas o operador marcou nem que a lista
    // está filtrada por busca, e exportaria a rota inteira. Paradas marcadas
    // mandam mais que a busca: marcar é uma escolha, buscar é só procurar.
    const recorte: RecorteDaTela = useMemo(() => {
        if (escolhidas.length > 0) {
            return {
                ids: escolhidas.map(uc => uc.chave),
                descricao: `${escolhidas.length} parada(s) marcadas na tela`,
                totalSemFiltro: ucs.length,
            };
        }
        return {
            ids: visiveis.map(uc => uc.chave),
            descricao: busca.trim() ? `busca "${busca.trim()}"` : '',
            totalSemFiltro: ucs.length,
        };
    }, [escolhidas, visiveis, busca, ucs.length]);

    useEffect(() => { onRecorteChange?.(recorte); }, [recorte, onRecorteChange]);

    const totalEscolhido = escolhidas.reduce((s, uc) => s + uc.valorTotal, 0);
    const economiaJuncao = useMemo(() => paradasEconomizadas(escolhidas), [escolhidas]);
    const semCoordenada = escolhidas.filter(uc => !uc.coordenadaConfiavel).length;
    const aGeocodificar = ucsQuePrecisamGeocode(escolhidas).length;

    // "sem localização" tem dois significados muito diferentes para quem vai
    // dirigir: a que ainda não foi buscada (a maioria — a busca só acontece ao
    // gerar a rota, para não gastar API paga em quem nem foi selecionado) e a
    // que não tem endereço aproveitável e nunca vai ser ordenada. Um Set porque
    // isto é lido uma vez por linha da tabela.
    const localizaveis = useMemo(
        () => new Set(ucsQuePrecisamGeocode(ucs).map(uc => uc.chave)),
        [ucs]);
    const todosVisiveisSelecionados = visiveis.length > 0 && visiveis.every(uc => selecionadas.has(uc.chave));

    /**
     * Todo código pelo qual uma parada pode ser chamada -> a chave dela.
     *
     * A planilha traz o código impresso na fatura, e a mesma unidade tem dois
     * números (`numero_uc` e `outro_numero_uc`): a lista pode estar exibindo
     * 355307501202 enquanto a planilha diz 10025066305. Indexar pelos códigos de
     * TODAS as faturas da parada faz os dois casarem — sem isso a importação
     * daria "não encontrada" para UCs que estão na tela.
     */
    const chavePorCodigo = useMemo(() => {
        const m = new Map<string, string>();
        ucs.forEach(uc => {
            const registrar = (codigo?: string | null) => {
                const k = normalizeUcCodigo(codigo);
                if (k) m.set(k, uc.chave);
            };
            registrar(uc.ucCodigo);
            uc.faturas.forEach(f => registrar(f.uc_codigo));
        });
        return m;
    }, [ucs]);

    /**
     * Marca as UCs que vieram da fila de cobrança manual.
     *
     * Roda quando `chavePorCodigo` fica pronto — antes disso a lista ainda não
     * carregou e a seleção casaria com nada. `consumirPreselecao` limpa ao ler,
     * então reabrir a Central depois não ressuscita uma seleção já usada.
     */
    useEffect(() => {
        if (chavePorCodigo.size === 0) return;
        const p = consumirPreselecao();
        if (!p) return;

        const achadas = new Set<string>();
        let faltando = 0;
        p.ucs.forEach(codigo => {
            const chave = chavePorCodigo.get(normalizeUcCodigo(codigo));
            if (chave) achadas.add(chave); else faltando++;
        });

        if (achadas.size === 0) {
            addToast(
                `As ${p.ucs.length} UCs da ${p.origem} não estão nesta lista. `
                + 'Confira o filtro de cidade e o período no topo.', 'error');
            return;
        }
        setSelecionadas(achadas);
        addToast(
            `${achadas.size} unidade(s) marcadas da ${p.origem}.`
            + (faltando > 0 ? ` ${faltando} não está(ão) nesta lista — verifique os filtros do topo.` : ''),
            faltando > 0 ? 'info' : 'success');
    }, [chavePorCodigo, addToast]);

    /** A prévia deixa de valer assim que a seleção muda: mostrar a rota antiga ao
     *  lado de uma seleção nova é como alguém sai para a rua com a lista errada. */
    const invalidarPrevia = () => { setPrevia(null); setRotaId(null); setMensagem(''); setLiberada(false); };

    /**
     * Marca na lista as UCs de uma planilha já refinada no Excel.
     *
     * SUBSTITUI a seleção em vez de somar: importar uma lista é dizer "é esta
     * aqui", e somar silenciosamente à seleção anterior levaria gente a mais na
     * viagem sem ninguém perceber.
     */
    const importarPlanilha = async (arquivo: File) => {
        setImportando(true);
        try {
            const { codigos, aba } = await lerUcsDaPlanilha(arquivo);
            const achadas = new Set<string>();
            let faltando = 0;
            codigos.forEach(codigo => {
                const chave = chavePorCodigo.get(codigo);
                if (chave) achadas.add(chave); else faltando++;
            });

            if (achadas.size === 0) {
                addToast(
                    `Nenhuma das ${codigos.length} UCs da planilha está nesta lista. `
                    + 'Confira o filtro de cidade e o período no topo da Central.', 'error');
                return;
            }

            setSelecionadas(achadas);
            invalidarPrevia();
            // O que falta é quase sempre filtro, não erro: a planilha de Goiânia
            // aberta com o filtro em Trindade casa pouca coisa. Dizer o número
            // deixa isso evidente sem precisar investigar.
            addToast(
                `${achadas.size} unidade(s) marcadas da aba "${aba}".`
                + (faltando > 0 ? ` ${faltando} não está(ão) nesta lista — verifique os filtros do topo.` : ''),
                faltando > 0 ? 'info' : 'success');
        } catch (e: any) {
            addToast(e?.message || 'Não consegui ler essa planilha.', 'error');
        } finally {
            setImportando(false);
        }
    };

    const alternar = (chave: string) => {
        setSelecionadas(prev => {
            const novo = new Set(prev);
            if (novo.has(chave)) novo.delete(chave); else novo.add(chave);
            return novo;
        });
        invalidarPrevia();
    };

    const alternarTodos = () => {
        setSelecionadas(prev => {
            if (todosVisiveisSelecionados) {
                const novo = new Set(prev);
                visiveis.forEach(uc => novo.delete(uc.chave));
                return novo;
            }
            return new Set([...prev, ...visiveis.map(uc => uc.chave)]);
        });
        invalidarPrevia();
    };

    const usarMinhaLocalizacao = async () => {
        setBuscandoGps(true);
        const pos = await getOrigin(8000);
        setBuscandoGps(false);
        if (!pos) { addToast('Não foi possível obter sua localização.', 'error'); return; }
        setOrigem(pos);
        setOrigemLabel('Minha localização');
        invalidarPrevia();
        addToast('Ponto de partida definido pela sua localização.', 'success');
    };

    const buscarSugestoes = async (texto: string) => {
        setEnderecoOrigem(texto);
        try { setSugestoes(await sugerirEnderecos(texto)); } catch { setSugestoes([]); }
    };

    const escolherSugestao = async (s: { placeId: string; text: string }) => {
        try {
            const pos = await localizarSugestao(s.placeId);
            if (!pos) { addToast('Não consegui localizar esse endereço.', 'error'); return; }
            setOrigem({ lat: pos.lat, lng: pos.lng });
            setOrigemLabel(pos.label || s.text);
            setEnderecoOrigem(pos.label || s.text);
            setSugestoes([]);
            invalidarPrevia();
        } catch { addToast('Não consegui localizar esse endereço.', 'error'); }
    };

    const buscarChegada = async (texto: string) => {
        setTextoChegada(texto);
        if (chegadaEndereco) { setChegadaEndereco(null); invalidarPrevia(); }
        try { setSugestoesChegada(await sugerirEnderecos(texto)); } catch { setSugestoesChegada([]); }
    };

    const escolherChegada = async (s: { placeId: string; text: string }) => {
        try {
            const pos = await localizarSugestao(s.placeId);
            if (!pos) { addToast('Não consegui localizar esse endereço.', 'error'); return; }
            const label = pos.label || s.text;
            setChegadaEndereco({ lat: pos.lat, lng: pos.lng, label });
            setTextoChegada(label);
            setSugestoesChegada([]);
            invalidarPrevia();
        } catch { addToast('Não consegui localizar esse endereço.', 'error'); }
    };

    const trocarModoChegada = (modo: ModoChegada) => {
        setModoChegada(modo);
        invalidarPrevia();
    };

    const gerarRota = async () => {
        if (escolhidas.length === 0) return;
        setGerando(true);
        invalidarPrevia();
        try {
            // Antes de gastar API: o cache de endereços já pode ter a coordenada.
            // É o que impede a UC sem cadastro de voltar ao Google a cada rota —
            // ela não tem onde guardar "já localizada" no próprio registro.
            setEtapa('Consultando endereços já localizados...');
            let lista = await hidratarCacheGeocode(escolhidas);

            const pendentes = ucsQuePrecisamGeocode(lista).length;
            if (pendentes > 0) {
                setEtapa(`Localizando ${pendentes} endereço${pendentes > 1 ? 's' : ''}...`);
                const res = await geocodificarUcs(lista);
                lista = lista.map(uc => aplicarGeocode(uc, res));
                if (res.consultados > 0) {
                    addToast(
                        `${res.gravadas} de ${res.consultados} endereços localizados (US$ ${res.custoUsd.toFixed(2)}).`,
                        res.gravadas > 0 ? 'success' : 'info');
                }
            }

            setEtapa('Calculando a melhor ordem...');
            const p = await montarPreviaRota(lista, {
                origem, origemLabel, destino, destinoLabel, app: appNav, agrupar: juntarPorCliente,
            });
            setPrevia(p);

            // Depois do paint: no mesmo tick a <section> da prévia ainda não existe.
            requestAnimationFrame(() =>
                previaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));

            // Sempre um toast no fim: a prévia surge abaixo da tabela e, mesmo com
            // a rolagem, sem confirmação escrita o clique fica parecendo perdido.
            if (p.paradas.length === 0) addToast('Nenhuma parada pôde ser montada.', 'error');
            else {
                const juntadas = p.ucsJuntadas > 0
                    ? ` ${p.ucsJuntadas} UC${p.ucsJuntadas > 1 ? 's' : ''} do mesmo cliente ${p.ucsJuntadas > 1 ? 'foram juntadas' : 'foi juntada'} numa parada.`
                    : '';
                if (p.semCoordenada.length > 0) {
                    addToast(
                        `Rota de ${p.paradas.length} paradas.${juntadas} ${p.semCoordenada.length} sem localização foram para o fim.`,
                        'info');
                } else {
                    addToast(`Rota de ${p.paradas.length} paradas pronta.${juntadas}`, 'success');
                }
            }
        } catch (e: any) {
            addToast(e?.message || 'Falha ao gerar a rota.', 'error');
        } finally {
            setGerando(false);
            setEtapa('');
        }
    };

    /**
     * Grava a rota e abre a conferência. A mensagem NÃO sai daqui.
     *
     * A rota nasce travada (`em_conferencia`). Montar a mensagem já neste ponto
     * deixava copiar e mandar para o cobrador antes de alguém olhar endereço e
     * material — e com o link do Maps de antes da correção do pino.
     */
    const salvarEConferir = async () => {
        if (!previa || !profile?.id) return;
        setSalvando(true);
        try {
            setEtapa('Salvando a rota...');
            const id = rotaId ?? await salvarRotaCobranca(previa, {
                responsavelId: responsavelEfetivo,
                criadaPor: profile.id,
                data: hojeLocalIso(),
                cidade: cidadeFiltro !== 'Todas' ? cidadeFiltro : null,
                nome: `Cobrança ${cidadeFiltro !== 'Todas' ? cidadeFiltro : 'geral'}`,
            });
            setRotaId(id);

            // `salvarRotaCobranca` devolve a rota IGUAL de hoje quando ela já
            // existe, e essa pode já ter sido liberada. Aí não há o que conferir.
            const salva = await carregarRotaCobranca(id);
            if (salva && salva.status !== 'em_conferencia') {
                addToast('Esta mesma rota já foi conferida e liberada hoje — reaproveitei.', 'info');
                await gerarMensagem(id, salva);
                return;
            }
            addToast('Rota salva e travada. Confira cada parada para liberar.', 'success');
            // A conferência abre sozinha: deixar para um segundo clique faria a
            // rota ficar esquecida no cadeado.
            setConferindo(id);
        } catch (e: any) {
            addToast(e?.message || 'Falha ao salvar a rota.', 'error');
        } finally {
            setSalvando(false);
            setEtapa('');
        }
    };

    /**
     * Monta a mensagem da rota JÁ LIBERADA.
     *
     * As paradas vêm da rota SALVA, não da prévia: na conferência dá para tirar
     * parada, tirar UC de uma parada e juntar clientes repetidos, e a mensagem
     * tem que sair com o que foi assinado — inclusive a numeração nova.
     *
     * Aplica também o que a conferência corrigiu: o pino arrastado passa a valer
     * no link do Maps de cada parada e nos trechos encadeados. A ORDEM não é
     * refeita — resequenciar depois de conferida trocaria a rota que alguém assinou.
     */
    const gerarMensagem = async (id: string, salva: RotaSalva) => {
        if (!previa) return;
        setSalvando(true);
        try {
            // Toda UC da rota salva estava na prévia (a conferência só tira ou
            // reagrupa o que já estava lá). A prévia vem primeiro porque é ela que
            // tem a coordenada recém-localizada.
            const porCodigo = new Map(ucs.map(uc => [uc.ucCodigo, uc] as const));
            previa.paradas.forEach(p => [p.uc, ...p.extras].forEach(uc => porCodigo.set(uc.ucCodigo, uc)));

            let perdidas = 0;
            const paradasConferidas: ParadaPrevia[] = salva.paradas.flatMap(sp => {
                const [principal, ...outras] = ucsDaParada(sp)
                    .map(u => porCodigo.get(u.uc_codigo))
                    .filter((uc): uc is UcInadimplente => !!uc);
                if (!principal) { perdidas++; return []; }

                let uc = principal;
                if (sp.endereco_situacao === 'corrigido' && sp.endereco_lat != null && sp.endereco_lng != null) {
                    const cadastro = {
                        ...(principal.cadastro ?? {}),
                        latitude: sp.endereco_lat,
                        longitude: sp.endereco_lng,
                        geocode_status: 'manual',
                    } as UcInadimplente['cadastro'];
                    uc = {
                        ...principal,
                        cadastro,
                        latitude: sp.endereco_lat,
                        longitude: sp.endereco_lng,
                        geocodeStatus: 'manual',
                        coordenadaConfiavel: true,
                    };
                }
                return [{
                    posicao: sp.posicao,
                    uc,
                    extras: outras,
                    urlMaps: urlNavegacaoUc(uc.cadastro, uc.faturas[0], appNav),
                }];
            });
            if (perdidas > 0) {
                addToast(
                    `${perdidas} parada(s) não estão mais na lista da Central (filtro do topo mudou?) e ficaram fora da mensagem.`,
                    'error');
            }
            setPrevia({ ...previa, paradas: paradasConferidas });

            setEtapa('Buscando o histórico de cada cliente...');
            // Independentes: em série a espera dobraria numa rota de 20 paradas.
            const [tratativas, termos] = await Promise.all([
                ultimaTratativaPorCpf(paradasConferidas.map(p => p.uc.cpfCnpj)),
                // O contrato assinado: é o que responde "eu nunca assinei" na porta.
                termosPorUc(paradasConferidas.map(p => p.uc)),
            ]);

            const opcoes = { baseUrl: baseUrlPublica(), rotaId: id, compacta: false };
            const paradasMensagem = paradasConferidas.map(p => {
                const todas = [p.uc, ...p.extras];
                const vencimentos = todas.map(u => u.vencMaisAntigo).filter((v): v is string => !!v).sort();
                return {
                    posicao: p.posicao,
                    ucCodigo: p.uc.ucCodigo,
                    titular: p.uc.titular,
                    clienteNome: p.uc.clienteNome,
                    telefones: p.uc.telefones,
                    qtdFaturas: faturasDaParada(p),
                    valorTotal: valorDaParada(p),
                    vencMaisAntigo: vencimentos[0] ?? null,
                    enderecoTexto: p.uc.enderecoTexto,
                    pdfFatura: p.uc.pdfFatura,
                    urlMaps: p.urlMaps,
                    faturaIds: todas.flatMap(u => u.faturas.map(f => f.id)),
                    ucsExtras: p.extras.map(e => ({ ucCodigo: e.ucCodigo, qtdFaturas: e.qtdFaturas, valorTotal: e.valorTotal })),
                    ultimaTratativa: p.uc.cpfCnpj ? tratativas.get(p.uc.cpfCnpj) ?? null : null,
                    termo: termos.get(p.uc.chave) ?? null,
                };
            });

            const cabecalho = cabecalhoRota({
                cidade: cidadeFiltro !== 'Todas' ? cidadeFiltro : null,
                data: hojeLocalIso(),
                paradas: paradasConferidas.length,
                valorTotal: paradasConferidas.reduce((s, p) => s + valorDaParada(p), 0),
            });
            // A chegada vai escrita: no Apple Maps e no Waze o link não a carrega.
            const chegada = salva.destino_label ? `
Chegada: ${salva.destino_label}` : '';

            // O formato cheio é o que o gestor especificou e continua sendo o
            // padrão. A versão curta só entra quando ela CABE no link do WhatsApp
            // e a cheia não — ver `escolherFormatoMensagem`. Se nem a curta cabe,
            // vai a cheia e o envio sai pela área de transferência.
            const cheia = `${cabecalho}${chegada}

${mensagemRota(paradasMensagem, opcoes)}`;
            const curta = `${cabecalho}${chegada}

${mensagemRota(paradasMensagem, { ...opcoes, compacta: true })}`;
            const usarCompacta = escolherFormatoMensagem(cheia, curta) === 'curta';
            setMensagem(usarCompacta ? curta : cheia);
            setLiberada(true);

            addToast(
                usarCompacta
                    ? `Rota liberada. ${paradasConferidas.length} paradas não cabem numa mensagem só — gerei a versão curta, com o link de cada parada.`
                    : 'Rota liberada. A mensagem para o cobrador está pronta.',
                'success');
        } catch (e: any) {
            addToast(e?.message || 'Falha ao montar a mensagem.', 'error');
        } finally {
            setSalvando(false);
            setEtapa('');
        }
    };

    const copiarMensagem = async () => {
        try {
            await copyToClipboard(mensagem);
            addToast('Mensagem copiada.', 'success');
        } catch {
            addToast('Não consegui copiar. Selecione o texto abaixo e copie à mão.', 'error');
        }
    };

    /**
     * Os trechos de até 10 paradas em que a rota cabe no Google Maps.
     *
     * O limite de 10 é do produto Google Maps (9 waypoints + destino), não nosso,
     * e não tem contorno por URL. `googleMapsDirTrechos` já quebrava a rota em
     * trechos encadeados — a última parada de um é a primeira do seguinte — mas a
     * tela abria só o primeiro e avisava num toast que sumia em segundos. Não
     * havia como chegar ao trecho 2. Agora eles viram botões que ficam na tela.
     */
    const trechosMaps = useMemo(() => {
        if (!previa || !suportaRotaEncadeada(appNav)) return [];
        // A posição anda junto com o ponto: o link pula quem não tem coordenada, e
        // o rótulo do trecho precisa apontar para a parada certa da lista.
        const comPonto = previa.paradas
            .filter(p => p.uc.coordenadaConfiavel && p.uc.latitude != null && p.uc.longitude != null)
            .map(p => ({ posicao: p.posicao, ponto: { lat: Number(p.uc.latitude), lng: Number(p.uc.longitude) } }));
        if (comPonto.length === 0) return [];
        return googleMapsDirTrechos(comPonto.map(x => x.ponto), previa.destino).map(t => ({
            ...t,
            // `ini`/`fim` já são 1-based sobre a lista filtrada.
            dePosicao: comPonto[t.ini - 1]?.posicao ?? t.ini,
            atePosicao: comPonto[t.fim - 1]?.posicao ?? t.fim,
        }));
    }, [previa, appNav]);

    const abrirRotaNoMaps = () => {
        if (!previa) return;
        const pontos = previa.paradas
            .filter(p => p.uc.coordenadaConfiavel && p.uc.latitude != null && p.uc.longitude != null)
            .map(p => ({ lat: Number(p.uc.latitude), lng: Number(p.uc.longitude) }));
        if (pontos.length === 0) { addToast('Nenhuma parada com localização para abrir no Maps.', 'info'); return; }

        // Só o Google Maps aceita paradas intermediárias por URL. No Apple Maps e
        // no Waze o link leva a UM destino, então o melhor que existe é mandar
        // para a primeira parada — as seguintes saem pelo link de cada linha.
        if (!suportaRotaEncadeada(appNav)) {
            window.open(navUrlCoordenada(pontos[0], appNav), '_blank');
            addToast(
                `${APPS_NAVEGACAO.find(a => a.valor === appNav)?.rotulo} não encadeia paradas num link só — `
                + 'abrindo a 1ª. Use o link de cada parada para as próximas.', 'info');
            return;
        }
        window.open(trechosMaps[0].url, '_blank');
    };

    return (
        <div className="p-6 space-y-4 pb-24">
            <header>
                <h2 className="text-lg font-black text-gray-800 tracking-tight">Agendamento de visita e tratativa presencial</h2>
                <p className="text-xs text-gray-500 mt-1 max-w-2xl leading-relaxed">
                    Escolha quem vai receber a visita, defina de onde sai e onde termina, e gere a ordem das paradas.
                    Depois da conferência, a rota vai para quem faz a visita com a mensagem pronta no WhatsApp.
                    A lista respeita os filtros do topo
                    {cidadeFiltro !== 'Todas' && <> — hoje, <strong className="text-gray-700">{cidadeFiltro}</strong></>}.
                </p>
            </header>

            {/* Quem vai fazer a rota — só aparece para quem pode atribuir */}
            {podeAtribuir && (
                <section className="p-4 bg-white rounded-xl border border-gray-200 space-y-2">
                    <div className="flex items-center gap-2 text-[10px] font-black uppercase text-gray-400 tracking-wider">
                        <UserCheck size={13} /> Quem vai fazer
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <select
                            value={responsavelId}
                            onChange={e => { setResponsavelId(e.target.value); invalidarPrevia(); }}
                            className="px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-[#5F6C37] sm:max-w-xs"
                        >
                            <option value="">Eu mesmo ({profile?.full_name || 'minha conta'})</option>
                            {equipe.filter(p => p.id !== profile?.id).map(p => (
                                <option key={p.id} value={p.id}>{p.full_name || p.id}</option>
                            ))}
                        </select>
                        <p className="text-[11px] text-gray-500 leading-relaxed">
                            Fica registrado que <strong className="text-gray-700">{profile?.full_name || 'você'}</strong> montou
                            e que <strong className="text-gray-700">{responsavelNome}</strong> executa.
                            A rota só chega para quem executa depois da conferência.
                        </p>
                    </div>
                </section>
            )}

            {/* Trajeto: de onde sai e onde termina */}
            <section className="bg-white rounded-xl border border-gray-200 grid grid-cols-1 lg:grid-cols-2 lg:divide-x divide-y lg:divide-y-0 divide-gray-100">
                {/* Partida */}
                <div className="p-4 space-y-3">
                    <div className="flex items-center gap-2 text-[10px] font-black uppercase text-gray-400 tracking-wider">
                        <span className="w-4 h-4 rounded-full bg-[#5F6C37] text-white flex items-center justify-center"><MapPin size={10} /></span>
                        Ponto de partida
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <button
                            onClick={usarMinhaLocalizacao}
                            disabled={buscandoGps}
                            className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#5F6C37] hover:bg-[#4a5429] text-white text-xs font-semibold transition-colors disabled:opacity-50 shrink-0"
                        >
                            {buscandoGps ? <Loader2 size={14} className="animate-spin" /> : <LocateFixed size={14} />}
                            Usar minha localização
                        </button>
                        <div className="relative flex-1">
                            <input
                                value={enderecoOrigem}
                                onChange={e => buscarSugestoes(e.target.value)}
                                placeholder="ou digite um endereço de partida"
                                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-[#5F6C37] focus:border-transparent outline-none"
                            />
                            {sugestoes.length > 0 && (
                                <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-56 overflow-y-auto">
                                    {sugestoes.map(s => (
                                        <button
                                            key={s.placeId}
                                            onClick={() => escolherSugestao(s)}
                                            className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors"
                                        >
                                            {s.text}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <p className="text-[11px] leading-relaxed">
                        {origemLabel
                            ? <span className="font-semibold text-[#5F6C37]">Partindo de: {origemLabel}</span>
                            : <span className="text-gray-500">Sem ponto de partida a rota começa pela primeira parada — definir a partida melhora a ordem.</span>}
                    </p>
                </div>

                {/* Chegada */}
                <div className="p-4 space-y-3">
                    <div className="flex items-center gap-2 text-[10px] font-black uppercase text-gray-400 tracking-wider">
                        <span className="w-4 h-4 rounded-full bg-gray-900 text-white flex items-center justify-center"><Flag size={9} /></span>
                        Ponto de chegada
                    </div>
                    <div role="radiogroup" aria-label="Onde a rota termina" className="inline-flex flex-wrap rounded-lg border border-gray-200 p-0.5 bg-gray-50">
                        {OPCOES_CHEGADA.map(op => (
                            <button
                                key={op.valor}
                                role="radio"
                                aria-checked={modoChegada === op.valor}
                                onClick={() => trocarModoChegada(op.valor)}
                                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                                    modoChegada === op.valor
                                        ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200'
                                        : 'text-gray-500 hover:text-gray-800'}`}
                            >
                                {op.rotulo}
                            </button>
                        ))}
                    </div>
                    {modoChegada === 'endereco' && (
                        <div className="relative">
                            <Flag size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            <input
                                value={textoChegada}
                                onChange={e => buscarChegada(e.target.value)}
                                placeholder="Digite onde a rota termina"
                                className="w-full pl-8 pr-8 py-2 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-[#5F6C37] focus:border-transparent outline-none"
                            />
                            {textoChegada && (
                                <button
                                    onClick={() => { setTextoChegada(''); setChegadaEndereco(null); setSugestoesChegada([]); invalidarPrevia(); }}
                                    aria-label="Limpar ponto de chegada"
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                                >
                                    <X size={14} />
                                </button>
                            )}
                            {sugestoesChegada.length > 0 && (
                                <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-56 overflow-y-auto">
                                    {sugestoesChegada.map(s => (
                                        <button
                                            key={s.placeId}
                                            onClick={() => escolherChegada(s)}
                                            className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors flex items-start gap-2"
                                        >
                                            <Flag size={11} className="text-gray-400 shrink-0 mt-0.5" /> {s.text}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <p className="text-[11px] leading-relaxed">
                        {modoChegada === 'ultima' && <span className="text-gray-500">A rota termina na última visita.</span>}
                        {modoChegada === 'partida' && (origem
                            ? <span className="font-semibold text-[#5F6C37]">Termina de volta em: {origemLabel || 'ponto de partida'}</span>
                            : <span className="font-semibold text-amber-700">Defina o ponto de partida ao lado para a rota saber para onde voltar.</span>)}
                        {modoChegada === 'endereco' && (chegadaEndereco
                            ? <span className="font-semibold text-[#5F6C37]">Termina em: {chegadaEndereco.label}</span>
                            : <span className="text-gray-500">Escolha um endereço da lista. A ordem das visitas passa a considerar esse final.</span>)}
                    </p>
                </div>
            </section>

            {/* Lista pronta e app de navegação */}
            <section className="p-4 bg-white rounded-xl border border-gray-200 flex flex-col sm:flex-row sm:items-end gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-[10px] font-black uppercase text-gray-400 tracking-wider mb-2">
                        <Upload size={13} /> Já tem a lista pronta?
                    </div>
                    <input
                        ref={inputPlanilhaRef}
                        type="file"
                        accept=".xlsx,.xlsm,.csv"
                        className="hidden"
                        onChange={e => {
                            const arquivo = e.target.files?.[0];
                            // Zera o input: escolher o MESMO arquivo de novo (depois
                            // de trocar o filtro de cidade) não dispara change algum.
                            e.target.value = '';
                            if (arquivo) importarPlanilha(arquivo);
                        }}
                    />
                    <button
                        onClick={() => inputPlanilhaRef.current?.click()}
                        disabled={importando}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-xs font-semibold text-gray-700 transition-colors disabled:opacity-50"
                    >
                        {importando ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                        {importando ? 'Lendo a planilha...' : 'Importar planilha e marcar'}
                    </button>
                    <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">
                        O Excel exportado por esta Central serve direto — a coluna <strong>UC</strong> é lida
                        e as unidades já ficam marcadas, substituindo a seleção atual.
                    </p>
                </div>

                <div className="sm:w-56 shrink-0">
                    <label htmlFor="app-navegacao" className="flex items-center gap-2 text-[10px] font-black uppercase text-gray-400 tracking-wider mb-2">
                        <Navigation size={13} /> Abrir mapas no
                    </label>
                    <select
                        id="app-navegacao"
                        value={appNav}
                        onChange={e => {
                            const escolhido = e.target.value as AppNavegacao;
                            setAppNav(escolhido);
                            salvarAppNavegacao(escolhido);
                            invalidarPrevia();
                        }}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-[#5F6C37]"
                    >
                        {APPS_NAVEGACAO.map(a => (
                            <option key={a.valor} value={a.valor}>{a.rotulo}</option>
                        ))}
                    </select>
                    <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">
                        {suportaRotaEncadeada(appNav)
                            ? 'Vale para os links da mensagem e para a rota inteira de uma vez.'
                            : 'No iPhone abre o app nativo. Só o Google Maps encadeia várias paradas num link — aqui a navegação é parada a parada.'}
                    </p>
                </div>
            </section>

            {/* Busca */}
            <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                    value={busca}
                    onChange={e => setBusca(e.target.value)}
                    placeholder="Buscar por cliente, UC, titular ou endereço..."
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-[#5F6C37] focus:border-transparent outline-none"
                />
            </div>

            {/* Tabela */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr className="text-left text-[10px] font-black uppercase text-gray-500 tracking-wider">
                                <th className="px-3 py-2.5 w-10">
                                    <input
                                        type="checkbox"
                                        checked={todosVisiveisSelecionados}
                                        onChange={alternarTodos}
                                        className="rounded border-gray-300 text-[#5F6C37] focus:ring-[#5F6C37] cursor-pointer"
                                        title="Selecionar todos os visíveis"
                                    />
                                </th>
                                <th className="px-3 py-2.5">UC / Cliente</th>
                                <th className="px-3 py-2.5">Endereço</th>
                                <th className="px-3 py-2.5 text-center">Faturas</th>
                                <th className="px-3 py-2.5 text-right">Valor</th>
                                <th className="px-3 py-2.5 text-center">Atraso</th>
                                <th className="px-3 py-2.5 text-center">Contato</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {visiveis.map(uc => {
                                const marcada = selecionadas.has(uc.chave);
                                const zap = linkWhatsappUc(uc.cadastro);
                                return (
                                    <tr
                                        key={uc.chave}
                                        onClick={() => alternar(uc.chave)}
                                        className={`cursor-pointer transition-colors ${marcada ? 'bg-[#5F6C37]/[0.06]' : 'hover:bg-gray-50'}`}
                                    >
                                        <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={marcada}
                                                onChange={() => alternar(uc.chave)}
                                                className="rounded border-gray-300 text-[#5F6C37] focus:ring-[#5F6C37] cursor-pointer"
                                            />
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <div className="font-bold text-gray-800 text-xs tabular-nums">{uc.ucCodigo}</div>
                                            <div className="text-[11px] text-gray-600 leading-snug">{uc.clienteNome}</div>
                                            {uc.titular && uc.titular.trim().toUpperCase() !== uc.clienteNome.trim().toUpperCase() && (
                                                <div className="text-[10px] text-gray-400">Titular: {uc.titular}</div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 max-w-[300px]">
                                            {uc.enderecoTexto
                                                ? <div className="text-[11px] text-gray-600 leading-snug">{uc.enderecoTexto}</div>
                                                : <span className="text-[11px] text-red-600 font-semibold">Sem endereço no cadastro</span>}
                                            {uc.enderecoTexto && !uc.coordenadaConfiavel && (
                                                localizaveis.has(uc.chave) ? (
                                                    <div className="flex items-center gap-1 text-[10px] text-gray-400 font-semibold mt-0.5">
                                                        <MapPin size={10} /> localização será buscada ao gerar a rota
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-1 text-[10px] text-amber-600 font-semibold mt-0.5">
                                                        <AlertTriangle size={10} /> endereço não localizável — vai para o fim da rota
                                                    </div>
                                                )
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 text-center text-xs font-semibold text-gray-700 tabular-nums">{uc.qtdFaturas}</td>
                                        <td className="px-3 py-2.5 text-right text-xs font-bold text-gray-800 tabular-nums whitespace-nowrap">{formatCurrency(uc.valorTotal)}</td>
                                        <td className="px-3 py-2.5 text-center">
                                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded tabular-nums ${uc.diasAtrasoMax >= 180 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                                {uc.diasAtrasoMax}d
                                            </span>
                                        </td>
                                        <td className="px-3 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                                            {uc.telefones.length > 0 ? (
                                                <div className="flex items-center justify-center gap-2">
                                                    <a href={`tel:${uc.telefones[0]}`} title={formatarTelefoneBr(uc.telefones[0])}
                                                        className="text-gray-400 hover:text-[#5F6C37] transition-colors"><Phone size={14} /></a>
                                                    {zap && (
                                                        <a href={zap} target="_blank" rel="noopener noreferrer" title="Abrir no WhatsApp"
                                                            className="text-gray-400 hover:text-green-600 transition-colors"><MessageCircle size={14} /></a>
                                                    )}
                                                </div>
                                            ) : <span className="text-[10px] text-gray-300">—</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                            {visiveis.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="px-3 py-12 text-center">
                                        <p className="text-sm text-gray-500 font-semibold">Nenhuma unidade inadimplente aqui.</p>
                                        <p className="text-xs text-gray-400 mt-1">
                                            {busca ? 'Tente outra busca.' : 'Ajuste os filtros no topo da Central.'}
                                        </p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Prévia da rota */}
            {previa && (
                <section ref={previaRef} className="p-4 bg-white rounded-xl border-2 border-[#5F6C37]/30 space-y-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <div className="font-black text-gray-800 text-sm tabular-nums">
                                Rota de {previa.paradas.length} parada{previa.paradas.length === 1 ? '' : 's'}
                                {previa.distanciaKm > 0 && ` · ${previa.distanciaKm.toFixed(1)} km`}
                                {previa.duracaoMin != null && ` · ~${Math.round(previa.duracaoMin)} min`}
                            </div>
                            <div className="text-[11px] text-gray-500 mt-0.5">
                                Ordem por {previa.fonte === 'google' ? 'trânsito real (Google)' : 'cálculo local'}
                                {previa.paradas.length > MAX_PARADAS_ROUTES && ' — acima de 24 paradas o Google não ordena'}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[11px]">
                                {previa.ucsJuntadas > 0 && (
                                    <span className="flex items-center gap-1 font-semibold text-[#5F6C37]">
                                        <Merge size={12} /> {previa.ucsJuntadas} UC{previa.ucsJuntadas > 1 ? 's' : ''} do mesmo cliente juntada{previa.ucsJuntadas > 1 ? 's' : ''}
                                    </span>
                                )}
                                {previa.destinoLabel && (
                                    <span className="flex items-center gap-1 font-semibold text-gray-700">
                                        <Flag size={11} /> chega em {previa.destinoLabel}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {trechosMaps.length > 1 ? (
                                // Mais de 10 paradas: o Google Maps não aceita num link só.
                                // Um botão por trecho, encadeados — o fim de um é o começo do
                                // seguinte, então dá para dirigir a rota inteira.
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-[10px] font-black uppercase tracking-wider text-gray-400">
                                        Maps · {trechosMaps.length} trechos
                                    </span>
                                    {trechosMaps.map((t, i) => (
                                        <a key={t.url} href={t.url} target="_blank" rel="noopener noreferrer"
                                            title={`Paradas ${t.dePosicao} a ${t.atePosicao}`}
                                            className="flex items-center gap-1 px-2.5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors">
                                            <Navigation size={13} /> {i + 1}
                                            <span className="text-[10px] font-normal opacity-80 tabular-nums">
                                                ({t.dePosicao}–{t.atePosicao})
                                            </span>
                                        </a>
                                    ))}
                                </div>
                            ) : (
                                <button onClick={abrirRotaNoMaps}
                                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors">
                                    <Navigation size={14} /> Abrir no Maps
                                </button>
                            )}
                            {!rotaId && (
                                <button onClick={salvarEConferir} disabled={salvando}
                                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#5F6C37] hover:bg-[#4a5429] text-white text-xs font-semibold transition-colors disabled:opacity-50">
                                    {salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                    {salvando ? etapa || 'Salvando...' : `Salvar e conferir · para ${responsavelNome}`}
                                </button>
                            )}
                        </div>
                    </div>

                    {trechosMaps.length > 1 && (
                        <p className="text-[11px] text-gray-500 leading-relaxed">
                            O Google Maps aceita no máximo {GOOGLE_MAPS_MAX_STOPS} paradas por link — é limite
                            do produto, não tem contorno. Os trechos acima são encadeados: a última parada de
                            um é a primeira do próximo, então dá para dirigir a rota inteira abrindo um de cada vez.
                        </p>
                    )}

                    <ol className="max-h-64 overflow-y-auto">
                        {previa.paradas.map(p => (
                            <li key={p.uc.chave} className="flex items-start gap-2.5 text-xs py-2 border-b border-gray-50 last:border-0">
                                <span className={`shrink-0 mt-px w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center tabular-nums ${p.uc.coordenadaConfiavel ? 'bg-[#5F6C37] text-white' : 'bg-amber-100 text-amber-700 border border-amber-300'}`}>
                                    {p.posicao}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-gray-800 truncate">
                                        {p.uc.ucCodigo} — {p.uc.clienteNome}
                                    </div>
                                    <div className="text-[11px] text-gray-500 truncate">
                                        {p.uc.enderecoTexto || <span className="text-red-600 font-semibold">sem endereço no cadastro</span>}
                                    </div>
                                    {p.extras.length > 0 && (
                                        <div className="mt-1 flex flex-wrap items-center gap-1">
                                            <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-[#5F6C37]">
                                                <Users size={10} /> cobra aqui também
                                            </span>
                                            {p.extras.map(e => (
                                                <span key={e.chave} className="px-1.5 py-0.5 rounded bg-[#5F6C37]/[0.08] text-[10px] font-semibold text-gray-700 tabular-nums">
                                                    UC {e.ucCodigo} · {formatCurrency(e.valorTotal)}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <span className="shrink-0 font-bold text-gray-700 tabular-nums">{formatCurrency(valorDaParada(p))}</span>
                                {p.urlMaps && (
                                    <a href={p.urlMaps} target="_blank" rel="noopener noreferrer" title="Navegar até aqui"
                                        className="shrink-0 text-gray-400 hover:text-blue-600 transition-colors"><ExternalLink size={13} /></a>
                                )}
                            </li>
                        ))}
                    </ol>

                    {/* Salva mas não liberada: nada para copiar ainda. O painel diz por
                        quê e leva de volta à conferência — fechar o modal sem querer não
                        pode deixar a rota esquecida no cadeado. */}
                    {rotaId && !liberada && (
                        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-gray-100">
                            <div className="flex items-start gap-2 min-w-0">
                                <Lock size={15} className="text-amber-600 shrink-0 mt-0.5" />
                                <p className="text-[11px] text-gray-600 leading-relaxed">
                                    <strong className="text-gray-800">Rota salva e travada.</strong> A mensagem do WhatsApp e o
                                    link do celular só saem depois de conferir endereço, boleto, fatura e termo de cada parada.
                                </p>
                            </div>
                            <button onClick={() => setConferindo(rotaId)} disabled={salvando}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gray-900 hover:bg-gray-800 text-white text-xs font-bold transition-colors disabled:opacity-50 shrink-0">
                                {salvando ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                                {salvando ? etapa || 'Montando a mensagem...' : 'Conferir e liberar'}
                            </button>
                        </div>
                    )}

                    {mensagem && (
                        <div className="space-y-2 pt-3 border-t border-gray-100">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="flex items-center gap-1 px-2 py-1 rounded-md bg-[#5F6C37]/10 text-[#5F6C37] text-[10px] font-black uppercase tracking-wider">
                                    <ShieldCheck size={12} /> Conferida e liberada
                                </span>
                                <button onClick={copiarMensagem}
                                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#5F6C37] hover:bg-[#4a5429] text-white text-xs font-semibold transition-colors">
                                    <Copy size={14} /> Copiar mensagem
                                </button>
                                <EnviarWhatsapp texto={mensagem} />
                                {rotaId && (
                                    <a href={`#/financeiro/rota-cobranca?rota=${rotaId}`} target="_blank" rel="noopener noreferrer"
                                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors">
                                        <Smartphone size={14} /> Abrir a rota no celular
                                    </a>
                                )}
                            </div>
                            {!cabeNoLinkWhatsapp(mensagem) && (
                                <p className="flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                    <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                                    <span>
                                        Esta mensagem tem {mensagem.length} caracteres, grande demais para ir por link.
                                        O botão do WhatsApp abre a janela de compartilhar: escolha o WhatsApp e a
                                        conversa. A mensagem também fica copiada — se não aparecer, cole.
                                    </span>
                                </p>
                            )}
                            <textarea
                                readOnly
                                value={mensagem}
                                rows={10}
                                onFocus={e => e.currentTarget.select()}
                                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[11px] font-mono bg-gray-50 leading-relaxed outline-none focus:ring-2 focus:ring-[#5F6C37]"
                            />
                        </div>
                    )}
                </section>
            )}

            {/* Barra da seleção — fica colada no rodapé enquanto houver escolha */}
            {escolhidas.length > 0 && (
                <div className="sticky bottom-4 z-10">
                    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-xl bg-[#1d220f] text-white shadow-2xl">
                        <div className="text-xs space-y-1">
                            <div>
                                <span className="font-black tabular-nums">{escolhidas.length}</span> unidade{escolhidas.length > 1 ? 's' : ''} ·{' '}
                                <span className="font-bold text-[#F6A937] tabular-nums">{formatCurrency(totalEscolhido)}</span>
                                {semCoordenada > 0 && (
                                    <span className="ml-2 text-amber-300">
                                        {semCoordenada} sem localização{aGeocodificar > 0 ? ` (${aGeocodificar} serão buscadas)` : ''}
                                    </span>
                                )}
                            </div>
                            {economiaJuncao > 0 && (
                                <label className="flex items-center gap-2 cursor-pointer text-[11px] text-white/80">
                                    <input
                                        type="checkbox"
                                        checked={juntarPorCliente}
                                        onChange={e => { setJuntarPorCliente(e.target.checked); invalidarPrevia(); }}
                                        className="rounded border-white/30 bg-white/10 text-[#5F6C37] focus:ring-[#5F6C37] cursor-pointer"
                                    />
                                    <span>
                                        Juntar UCs do mesmo CPF/CNPJ numa parada
                                        <span className="ml-1 font-bold text-white tabular-nums">
                                            ({juntarPorCliente ? `${economiaJuncao} parada${economiaJuncao > 1 ? 's' : ''} a menos` : 'desligado'})
                                        </span>
                                    </span>
                                </label>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => { setSelecionadas(new Set()); invalidarPrevia(); }}
                                className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-semibold transition-colors">
                                Limpar
                            </button>
                            <button onClick={gerarRota} disabled={gerando}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#5F6C37] hover:bg-[#4a5429] text-xs font-bold transition-colors disabled:opacity-50">
                                {gerando ? <Loader2 size={14} className="animate-spin" /> : <RouteIcon size={14} />}
                                {gerando ? etapa || 'Gerando...' : 'Gerar rota otimizada'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {conferindo && (
                <ConferenciaRotaModal
                    rotaId={conferindo}
                    onFechar={() => setConferindo(null)}
                    onLiberada={async () => {
                        // Relê a rota: é nela que estão os pinos corrigidos na conferência.
                        const id = conferindo;
                        try {
                            const salva = await carregarRotaCobranca(id);
                            if (salva) await gerarMensagem(id, salva);
                        } catch (e: any) {
                            addToast(e?.message || 'Rota liberada, mas não consegui montar a mensagem.', 'error');
                        }
                    }}
                />
            )}
        </div>
    );
};

export default RotaCobranca;
